import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { APPLICATION_STATUSES } from "@/lib/constants";
import { titleCase } from "@/lib/format";
import { useEntitlements } from "@/lib/useEntitlements";
import { Link } from "react-router-dom";
import { Sparkle, FileText, Warning, Lock } from "@phosphor-icons/react";

const NONE = "__none__";

export default function ApplicationDrawer({ open, onOpenChange, job, application, onSaved }) {
  const { can } = useEntitlements();
  const canPack = can("can_generate_application_pack");
  const [status, setStatus] = useState("interested");
  const [notes, setNotes] = useState("");
  const [cvDoc, setCvDoc] = useState(NONE);
  const [coverLetter, setCoverLetter] = useState("");
  const [docs, setDocs] = useState([]);
  const [gen, setGen] = useState(false);

  useEffect(() => {
    if (open) {
      api.get("/documents").then((r) => setDocs(r.data.documents));
      if (application) {
        setStatus(application.status || "interested");
        setNotes(application.notes || "");
        setCvDoc(application.cv_document_id || NONE);
        setCoverLetter(application.cover_letter || "");
      }
    }
  }, [open, application]);

  const save = async () => {
    try {
      await api.post("/applications", {
        job_id: job.id, status, notes,
        cv_document_id: cvDoc === NONE ? null : cvDoc, cover_letter: coverLetter,
      });
      toast.success("Application saved");
      onSaved && onSaved();
      onOpenChange(false);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const generate = async (kind) => {
    setGen(true);
    try {
      const { data } = await api.post(`/applications/generate-${kind}`, { job_id: job.id });
      if (kind === "cover-letter") setCoverLetter(data.content);
      else { setCoverLetter((c) => c); toast.success("CV draft ready — copied into notes for review"); setNotes((n) => (data.content)); }
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setGen(false); }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto" data-testid="application-drawer">
        <SheetHeader>
          <SheetTitle className="font-heading">Application workspace</SheetTitle>
        </SheetHeader>
        <p className="text-sm text-slate-600 mt-1">{job.title} · {job.employer}</p>

        <div className="mt-6 space-y-5">
          <div>
            <Label>Status</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="mt-1.5" data-testid="app-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {APPLICATION_STATUSES.map((s) => <SelectItem key={s} value={s}>{titleCase(s)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>CV / document to use</Label>
            <Select value={cvDoc} onValueChange={setCvDoc}>
              <SelectTrigger className="mt-1.5" data-testid="app-cv-select"><SelectValue placeholder="Select a document" /></SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE}>None selected</SelectItem>
                {docs.map((d) => <SelectItem key={d.id} value={d.id}>{d.label}</SelectItem>)}
              </SelectContent>
            </Select>
            {docs.length === 0 && <p className="mt-1 text-xs text-slate-400">Upload documents in the Documents section first.</p>}
          </div>

          <div className="rounded-md border border-slate-200 bg-slate-50 p-3">
            <div className="flex items-start gap-2 text-xs text-slate-600">
              <Warning weight="fill" className="text-orange-500 mt-0.5 shrink-0" />
              <p>Drafts are built only from verified facts in your profile. Nothing is invented — you review and approve before use.</p>
            </div>
            {canPack ? (
              <div className="mt-3 flex gap-2">
                <Button variant="outline" size="sm" onClick={() => generate("cv")} disabled={gen} data-testid="generate-cv-btn" className="border-slate-300 gap-1.5">
                  <FileText /> Draft CV
                </Button>
                <Button variant="outline" size="sm" onClick={() => generate("cover-letter")} disabled={gen} data-testid="generate-cover-btn" className="border-slate-300 gap-1.5">
                  <Sparkle weight="fill" /> Draft cover letter
                </Button>
              </div>
            ) : (
              <div className="mt-3 flex items-center justify-between gap-2 rounded-md border border-indigo-200 bg-indigo-50 px-3 py-2" data-testid="app-pack-locked">
                <span className="text-xs text-indigo-800 flex items-center gap-1.5"><Lock weight="fill" /> CV & cover-letter drafting is a Pro feature.</span>
                <Link to="/pricing"><Button size="sm" className="bg-indigo-600 hover:bg-indigo-700 h-7 text-xs">Upgrade</Button></Link>
              </div>
            )}
          </div>

          <div>
            <Label>Cover letter</Label>
            <Textarea value={coverLetter} onChange={(e) => setCoverLetter(e.target.value)} rows={8} className="mt-1.5" placeholder="Write or generate a cover letter…" data-testid="app-cover-letter" />
          </div>

          <div>
            <Label>Notes</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={4} className="mt-1.5" placeholder="Application notes, draft CV text, answers to questions…" data-testid="app-notes" />
          </div>

          <Button onClick={save} data-testid="save-application-btn" className="w-full bg-slate-900 hover:bg-slate-800">Save application</Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
